import { FC, useContext, useMemo } from 'react';
import {
  Box,
  Button,
  Container,
  Divider,
  Icon,
  StackDivider,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import {
  FarmTeaserFragment,
  FilterInputGrouped,
  ParagraphSearchResultFragment,
  TermCategory,
  TermCategoryFragment,
} from '../../../generated/types';
import { FarmTeaser } from '../../Teaser/FarmTeaser';
import Pagination from '../../Common/Pagination';
import { SearchContext } from '../../../context/SearchContext';
import { IoClose } from '@react-icons/all-files/io5/IoClose';
import { useRouter } from 'next/router';
import getFilterGroups from '../../../utils/getFilterGroups';
import { filtersToQuery } from '../../../utils/filters';
import { addParamsToPath } from '../../../utils/urls';

interface SearchResultProps {
  data: ParagraphSearchResultFragment;
}

export const SearchResult: FC<SearchResultProps> = ({ data }) => {
  const router = useRouter();

  const sortedData = useMemo(() => {
    const groups: Record<'address' | 'premium', FarmTeaserFragment[]> = {
      address: [],
      premium: [],
    };

    let sorted: FarmTeaserFragment[] = [];

    data.result?.items?.map((farm) => {
      if (farm.listingType.title === 'Address') {
        groups.address.push(farm);
      } else {
        groups.premium.push(farm);
      }
    });

    // First Address
    if (groups.address.length >= 1) {
      sorted.push(groups.address.shift() as FarmTeaserFragment);
    }

    // Then 2 Premium
    for (let i = 0; i < 2; i++) {
      sorted.push(groups.premium.shift() as FarmTeaserFragment);
    }

    // Then again address
    if (groups.address.length >= 1) {
      sorted.push(groups.address.shift() as FarmTeaserFragment);
    }

    // Fill with premium and address
    // if we still have address entries (then we didnt have enough premium), just add them
    sorted = [...sorted, ...groups.premium, ...groups.address];

    return sorted.filter(Boolean);
  }, [data]);

  const searchContext = useContext(SearchContext);

  const onRemoveFilter = (filter: TermCategoryFragment) => {
    const remainingFilters = searchContext.activeFilter?.filter(
      (activeFilter) => activeFilter.id !== filter.id
    );

    if (remainingFilters) {
      const filterGroups: FilterInputGrouped[] = getFilterGroups(
        searchContext.searchMoreFilter!,
        remainingFilters.map((filter) => filter.id)
      );
      const filterQuery = filtersToQuery(filterGroups);
      const newPath = addParamsToPath(router.asPath, {
        filters: filterQuery,
        page: null,
      });

      router.push(newPath);
    }
  };

  return (
    <Box className={data.__typename} mt={12}>
      <Container maxW="container.main" py={3}>
        {searchContext.activeFilter && (
          <Wrap mb={6}>
            {searchContext.activeFilter.map((filter, i) => {
              return (
                <WrapItem
                  key={filter.id}
                  size="sm"
                  alignItems="center"
                  as={Button}
                  borderRadius="full"
                  border="1px solid"
                  borderColor="primary"
                  bg="primaryLight50"
                  color="primary"
                  px={3}
                  py={1}
                  onClick={() => {
                    onRemoveFilter(filter);
                  }}
                >
                  {filter.title}
                  <Icon ml={1} as={IoClose} />
                </WrapItem>
              );
            })}
          </Wrap>
        )}

        <Box fontSize="lg" fontWeight="bold" color="primary">
          {data.result?.total} Ergebnisse gefunden
        </Box>

        <VStack
          mt={12}
          alignItems="flex-start"
          spacing={9}
          divider={<StackDivider borderColor="gray.300" />}
        >
          {sortedData.map((farm) => (
            <FarmTeaser key={farm.id} data={farm} />
          ))}
        </VStack>
        {data.result?.total && data.result?.total > sortedData.length && (
          <Box>
            <Divider borderColor="gray.300" opacity={1} my={12} />
            <Pagination total={data.result.total} perPage={6} />
          </Box>
        )}
      </Container>
    </Box>
  );
};
